import { Close, Menu, Search } from '@material-ui/icons'
import classnames from 'classnames'
import * as React from 'react'
import { useState } from 'react'
import styled from 'styled-components'

import { useMainMenuNavigationData } from '../../hooks/useMainMenuNavigationData'
import Logo from '../../images/svg/motat_header_new.svg'
import SixtyYearsOnlyLogo from '../../images/svg/sixty_years_only.svg'
import SixtyYearsLogo from '../../images/svg/sixty_years_web_banner_white.svg'
import { navigationMock } from '../../mocks/navigation'
import Link from '../atoms/link'
import StickyHeader from '../atoms/sticky-header'
import theme from '../atoms/theme'
import Container from '../container'
import Button from '../molecules/button'

import { navigationVars } from '../../helpers/navigationVars'
import { DesktopOnly } from '../atoms/style-helpers'
import NavigationItem, { SubNavigationItem } from './navigation-item'
import Notification from './notification'

interface NavigationProps {
  onNotification: (b: boolean) => void
}

const { breakpoints, colours, animation, font } = theme

const NavigationContainer = styled.div`
  background-color: ${colours.dark};
`

const NavigationItemsContainer = styled.div`
  @media only screen and ${breakpoints.toNormalScreen} {
    position: absolute;
    left: 100%;
    width: 100%;
    top: ${navigationVars.mobileHeight};
    background-color: ${colours.dark};
    transition: left ${animation.defaultLength} ease-out;
    min-height: calc(100vh - ${navigationVars.mobileHeight});
    max-height: calc(100vh - ${navigationVars.mobileHeight});
    overflow-y: scroll;
    overflow-x: hidden;
  }

  &.is-active {
    left: 0;
  }
`

const NavigationItems = styled.ul`
  display: flex;
  margin: 0;
  align-items: flex-start;

  @media only screen and ${breakpoints.toNormalScreen} {
    flex-direction: column;
    display: flex;
    padding: 0;
  }

  @media only screen and ${breakpoints.fromNormalScreen} {
    position: relative;
  }
`

export const LogoContainer = styled.div`
  padding-bottom: 0.2rem;
`

export const MotatLogo = styled.div`
  height: 3.47rem;
  display: flex;
  gap: 1.2rem;

  svg,
  a {
    height: 100%;
  }

  @media only screen and ${breakpoints.fromNormalScreen} {
    height: 5.34rem;
  }
`

const FlexContainer = styled.div`
  display: flex;
  gap: 1.2rem;
  align-items: center;
  justify-content: space-between;
  height: ${navigationVars.mobileHeight};

  @media only screen and ${breakpoints.fromNormalScreen} {
    height: ${navigationVars.desktopHeight};
  }
`

const RightNavigation = styled.div`
  height: ${navigationVars.mobileHeight};
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media only screen and ${breakpoints.fromNormalScreen} {
    justify-content: flex-end;
    height: ${navigationVars.desktopHeight};
  }
`

const SecondaryNavigation = styled(SubNavigationItem)`
  justify-content: flex-end;
  align-items: center;
  display: none;

  a {
    color: ${colours.light};
    font-size: ${font.fontSizeSmall};
    line-height: ${font.fontSizeDesktop};
    margin-bottom: 1.8rem;
    display: flex;
    align-items: center;
    margin-left: 3.6rem;
    padding: 0;
    font-weight: ${font.fontWeightNormal};

    .MuiSvgIcon-root {
      width: 2.4rem;
      height: 2.4rem;
      margin-left: 1.2rem;
    }
  }

  @media only screen and ${breakpoints.fromNormalScreen} {
    display: flex;
  }
`

const NavigationItemButton = styled(Button)`
  position: relative;
  top: -0.3rem;
  margin: 0 0 0 1rem;
  @media only screen and ${breakpoints.toNormalScreen} {
    top: 0.5rem;
  }
`

const MobileNavigationItems = styled.div`
  display: none;

  .MuiSvgIcon-root {
    width: 3.2rem;
    height: 3.2rem;
    margin-left: 1.2rem;
  }

  .mobile-search {
    width: 2.4rem;
    height: 2.4rem;
    margin-left: 1.2rem;
  }

  .mobile-annualpass {
    color: ${colours.light};
    font-size: ${font.fontSizeSmall};
    line-height: ${font.fontSizeNavigationMobile};
    font-weight: ${font.fontWeightNormal};
    text-decoration: none;
  }

  @media only screen and ${breakpoints.toNormalScreen} {
    color: ${colours.light};
    display: flex;
    align-items: center;
    cursor: pointer;
  }
`

const SearchLink = styled(Link)`
  display: flex;
`

const Navigation = (props: NavigationProps): JSX.Element => {
  const [isMenuActive, setMenuActive] = useState(false)

  const navigationContentful = useMainMenuNavigationData()

  let navigation = navigationMock

  if (navigationContentful.nodes.length > 0) {
    const navigationTransformed =
      navigationContentful?.nodes[0]?.navigationItems?.map((navItem) => {
        const menuItemsTransformed = navItem?.menuItems?.map((menuItem) => {
          return {
            label: menuItem.title,
            url: menuItem.url,
          }
        })

        return {
          label: navItem.title,
          url: navItem.url,
          items: menuItemsTransformed,
        }
      })

    navigation = navigationTransformed
  }

  const menuClass = classnames({
    'is-active': isMenuActive,
  })

  return (
    <StickyHeader>
      <Notification
        isMenuActive={isMenuActive}
        layoutNotification={props.onNotification}
      />
      <nav>
        <NavigationContainer>
          <Container>
            <FlexContainer>
              <LogoContainer>
                <MotatLogo>
                  <Link to={'/'} aria-label={'MOTAT, Science + Tech + Play'}>
                    <Logo />
                  </Link>
                </MotatLogo>
              </LogoContainer>
              <RightNavigation>
                <SecondaryNavigation>
                  <Link to='/visit/annual-pass/' aria-label='Annual Pass'>
                    Annual Pass
                  </Link>
                  <DesktopOnly>
                    <Link to='/support-and-join/donate/' aria-label='Donate'>
                      Donate
                    </Link>
                  </DesktopOnly>
                  <Link to='/search/' aria-label='Search'>
                    Search <Search />
                  </Link>
                </SecondaryNavigation>
                <NavigationItemsContainer className={menuClass}>
                  <NavigationItems>
                    {navigation?.map((menuItem) => {
                      // Create a unique key based on the menuItem's label and url
                      const uniqueKey = `${menuItem.label}-${menuItem.url}`

                      return (
                        <NavigationItem menuItem={menuItem} key={uniqueKey} />
                      )
                    })}
                    <li>
                      <NavigationItemButton
                        type='secondary'
                        to='https://my.motat.nz/events'
                        small={true}
                      >
                        TICKETS
                      </NavigationItemButton>
                    </li>
                  </NavigationItems>
                </NavigationItemsContainer>
                <MobileNavigationItems>
                  <Link
                    to='/visit/annual-pass/'
                    className='mobile-annualpass'
                    aria-label='Annual Pass'
                  >
                    Annual Pass
                  </Link>
                  <SearchLink to='/search/' aria-label='Search'>
                    <Search className='mobile-search' />
                  </SearchLink>
                  {isMenuActive ? (
                    <Close
                      className='close'
                      onClick={(): void => setMenuActive(false)}
                    />
                  ) : (
                    <Menu
                      className='menu'
                      onClick={(): void => setMenuActive(true)}
                    />
                  )}
                </MobileNavigationItems>
              </RightNavigation>
            </FlexContainer>
          </Container>
        </NavigationContainer>
      </nav>
    </StickyHeader>
  )
}

export default Navigation
